import React, { useMemo } from 'react';
import PageLayout from '@/components/layout/PageLayout';
import { PermissionGuard } from '@/components/layout';
import { TabList } from '@/components/ui/navigation';
import { PrimaryButton } from '@/components/ui/buttons';
import { IconScout } from '@/components/ui/images/IconScout';
import { ExportDropdown } from '@/components/ui/filters/ExportDropdown';
import { Skeleton } from '@/components/ui/loading';
import { Text } from '@/components/ui/text/Text';
import { PlaceholderContent } from '@/components/ui/layout';
import {
  AttendanceSituationCard,
  AttendanceSituationCardSkeleton,
  TodayMarksCard,
  WeeklyAttendanceCard,
  MonthSummarySection,
  MyTeamTabContent,
} from '@/components/features/attendance/dashboard';
import { useTranslation } from '@/i18n';
import { useAttendanceDashboard } from '@/hooks/attendance/dashboard/useAttendanceDashboard';
import type { DashboardTab } from '@/hooks/attendance/dashboard/useAttendanceDashboard';
import type {
  AttendanceDashboard as AttendanceDashboardData,
  DashboardMark,
  DashboardMonthly,
  DashboardWeekly,
  MonthlyCutCompanyScope,
} from '@/api/domains/attendance';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { colors } from '@/assets/styles/colors';
import type { UIExportFormat } from '@/types/fileExport.types';


interface MiAsistenciaTabContentProps {
  dashboard: AttendanceDashboardData | null;
  weeklyData: DashboardWeekly | null;
  monthly: DashboardMonthly | null;
  todayMarks: DashboardMark[];
  isLoadingMain: boolean;
  isLoadingMarks: boolean;
  isLoadingMonthly: boolean;
  isLoadingWeekly: boolean;
  isLoadingCutoffs: boolean;
  situationDateLabel: string;
  selectedYear: string;
  selectedMonth: string;
  selectedCutId: string;
  yearOptions: { value: string; label: string }[];
  monthOptions: { value: string; label: string }[];
  cutOptions: { value: string; label: string }[];
  isCutSelectorDisabled: boolean;
  setSelectedYear: (year: string) => void;
  setSelectedMonth: (month: string) => void;
  setSelectedCutId: (cutId: string) => void;
  companyScope: MonthlyCutCompanyScope;
  setCompanyScope: (scope: MonthlyCutCompanyScope) => void;
  companyScopeOptions: { value: MonthlyCutCompanyScope; label: string }[];
  onExportDashboard: (format: UIExportFormat) => Promise<void>;
  isExportingDashboard: boolean;
}

const MiAsistenciaTabContent: React.FC<MiAsistenciaTabContentProps> = ({
  dashboard,
  weeklyData,
  monthly,
  todayMarks,
  isLoadingMain,
  isLoadingMarks,
  isLoadingMonthly,
  isLoadingWeekly,
  isLoadingCutoffs,
  situationDateLabel,
  selectedYear,
  selectedMonth,
  selectedCutId,
  yearOptions,
  monthOptions,
  cutOptions,
  isCutSelectorDisabled,
  setSelectedYear,
  setSelectedMonth,
  setSelectedCutId,
  companyScope,
  setCompanyScope,
  companyScopeOptions,
  onExportDashboard,
  isExportingDashboard,
}) => {
  const { t: translate } = useTranslation();

  const scheduleTheoretical = useMemo(() => {
    if (!dashboard) return '';
    const {
      isFreeSchedule, hasSchedule, scheduleEntry, scheduleExit, lunchBreak,
    } = dashboard.situation;
    if (isFreeSchedule) return translate('attendance:dashboard.schedule.free');
    if (!hasSchedule) return translate('attendance:dashboard.schedule.noSchedule');
    if (lunchBreak) return translate('attendance:dashboard.schedule.theoreticalWithLunch', { entry: scheduleEntry, exit: scheduleExit, lunch: lunchBreak });
    return translate('attendance:dashboard.schedule.theoretical', { entry: scheduleEntry, exit: scheduleExit });
  }, [dashboard, translate]);

  const scheduleReal = useMemo(() => {
    if (!dashboard) return '';
    const { marksEntry, marksExit } = dashboard.situation;
    if (marksEntry && marksExit) return translate('attendance:dashboard.schedule.realWithExit', { entry: marksEntry, exit: marksExit });
    if (marksEntry) return translate('attendance:dashboard.schedule.realNoExit', { entry: marksEntry });
    return translate('attendance:dashboard.marks.noMarks');
  }, [dashboard, translate]);

  const translatedMetrics = useMemo(
    () => dashboard?.situation.metrics.map((m) => ({
      label: translate(m.labelKey),
      value: m.value,
    })) ?? [],
    [dashboard, translate],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text
          variant="body-medium"
          fontFamily="montserrat"
          color={colors.grays.neutral66}
          style={{ fontWeight: 600 }}
          data-testid="situation-section-title"
        >
          {translate('attendance:dashboard.situationTitle')}{' '}
          <span style={{ fontWeight: 400 }}>{situationDateLabel}</span>
        </Text>
        <PermissionGuard action="view" fallback="hidden">
          <ExportDropdown
            onExport={(format) => void onExportDashboard(format)}
            disabled={!dashboard}
            isLoading={isExportingDashboard}
            placement="right"
            availableFormats={['PDF', 'EXCEL']}
            data-testid="situation-download-button"
          />
        </PermissionGuard>
      </div>

      {(isLoadingMain || !dashboard) ? (
        <AttendanceSituationCardSkeleton data-testid="situation-card-skeleton" />
      ) : (
        <AttendanceSituationCard
          status={dashboard.situation.status}
          statusLabel={dashboard.situation.statusLabel}
          scheduleTheoretical={scheduleTheoretical}
          scheduleReal={scheduleReal}
          metrics={translatedMetrics}
          data-testid="situation-card"
        />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        <TodayMarksCard
          marks={todayMarks}
          isLoading={isLoadingMarks}
          data-testid="today-marks-card"
        />
        {isLoadingWeekly || !weeklyData
          ? <Skeleton height={200} data-testid="weekly-card-skeleton" />
          : (
            <WeeklyAttendanceCard
              days={weeklyData.days}
              workedHours={weeklyData.workedHours}
              totalHours={weeklyData.totalHours}
              data-testid="weekly-card"
            />
          )
        }
      </div>

      {(isLoadingCutoffs || selectedYear !== '' || companyScope === 'PARENT') && (
        <MonthSummarySection
          data={monthly}
          isLoadingData={isLoadingMonthly}
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          selectedCutId={selectedCutId}
          yearOptions={yearOptions}
          monthOptions={monthOptions}
          cutOptions={cutOptions}
          isCutSelectorDisabled={isCutSelectorDisabled}
          onYearChange={setSelectedYear}
          onMonthChange={setSelectedMonth}
          onCutChange={setSelectedCutId}
          isLoadingFilters={isLoadingCutoffs}
          companyScope={companyScope}
          onCompanyScopeChange={setCompanyScope}
          companyScopeOptions={companyScopeOptions}
          data-testid="month-summary-section"
        />
      )}
    </div>
  );
};

export const AttendanceDashboard: React.FC = () => {
  const { t: translate } = useTranslation();
  const {
    employeeFirstName,
    activeTab,
    setActiveTab,
    dashboard,
    weeklyData,
    monthly,
    todayMarks,
    isLoadingMain,
    isLoadingMarks,
    isLoadingMonthly,
    isLoadingWeekly,
    isLoadingCutoffs,
    lastUpdate,
    situationDateLabel,
    selectedYear,
    selectedMonth,
    selectedCutId,
    lastOpenCutYear,
    lastOpenCutMonth,
    yearOptions,
    monthOptions,
    cutOptions,
    isCutSelectorDisabled,
    setSelectedYear,
    setSelectedMonth,
    setSelectedCutId,
    companyScope,
    setCompanyScope,
    companyScopeOptions,
    refresh,
    canManualRefresh,
    isActiveTabReady,
    registerMyTeamLoading,
    registerMyTeamRefetch,
    onExportDashboard,
    isExportingDashboard,
  } = useAttendanceDashboard();
  const { canView } = usePagePermissions();

  const tabs = !canView ? [] : [
    {
      id: 'mi-asistencia' as DashboardTab,
      label: translate('attendance:dashboard.tabs.myAttendance'),
      content: (
        <MiAsistenciaTabContent
          dashboard={dashboard}
          weeklyData={weeklyData}
          monthly={monthly}
          todayMarks={todayMarks}
          isLoadingMain={isLoadingMain}
          isLoadingMarks={isLoadingMarks}
          isLoadingMonthly={isLoadingMonthly}
          isLoadingWeekly={isLoadingWeekly}
          isLoadingCutoffs={isLoadingCutoffs}
          situationDateLabel={situationDateLabel}
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          selectedCutId={selectedCutId}
          yearOptions={yearOptions}
          monthOptions={monthOptions}
          cutOptions={cutOptions}
          isCutSelectorDisabled={isCutSelectorDisabled}
          setSelectedYear={setSelectedYear}
          setSelectedMonth={setSelectedMonth}
          setSelectedCutId={setSelectedCutId}
          companyScope={companyScope}
          setCompanyScope={setCompanyScope}
          companyScopeOptions={companyScopeOptions}
          onExportDashboard={onExportDashboard}
          isExportingDashboard={isExportingDashboard}
        />
      ),
    },
    {
      id: 'mi-equipo' as DashboardTab,
      label: translate('attendance:dashboard.tabs.myTeam'),
      content: (
        <MyTeamTabContent
          onLoadingChange={registerMyTeamLoading}
          onRefetchReady={registerMyTeamRefetch}
        />
      ),
    },
    {
      id: 'empresa' as DashboardTab,
      label: translate('attendance:dashboard.tabs.company'),
      content: (
        <PlaceholderContent label={translate('attendance:dashboard.tabs.company')} />
      ),
    },
  ];

  return (
    <PageLayout hideBreadcrumb hideCard>
      <div
        style={{ display: 'flex', flexDirection: 'column' }}
        data-testid="attendance-dashboard-page"
      >
        {tabs.length === 0 ? (
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center', padding: '48px 0' }}
            data-testid="dashboard-no-access"
          >
            <Text variant="body-medium" fontFamily="montserrat" style={{ fontWeight: 600 }}>
              {translate('attendance:dashboard.noAccess.title')}
            </Text>
            <Text variant="body-regular" color={colors.grays.neutral66}>
              {translate('attendance:dashboard.noAccess.description')}
            </Text>
          </div>
        ) : (
          <>
            <div
              className="-mx-4 px-4 md:-mx-8 md:px-8 lg:-mx-16 lg:px-16"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                height: 150,
                paddingTop: 32,
                backgroundColor: colors.grays.neutralFF,
                border: 'none',
              }}
              data-testid="dashboard-header-section"
            >
              {employeeFirstName && (
                <Text
                  variant="body-medium"
                  fontFamily="montserrat"
                  color={colors.primary.dark}
                  style={{ fontSize: 25, fontWeight: 600 }}
                  data-testid="dashboard-greeting"
                >
                  {translate('attendance:dashboard.greeting', { name: employeeFirstName })}
                </Text>
              )}
              {isLoadingCutoffs ? (
                <Skeleton width={280} height={20} />
              ) : (lastOpenCutYear && lastOpenCutMonth) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Text
                    variant="body-regular"
                    fontFamily="montserrat"
                    color={colors.primary.dash}
                    style={{ fontSize: 16, fontWeight: 400 }}
                    data-testid="dashboard-last-open-cutoff-label"
                  >
                    {translate('attendance:dashboard.lastOpenCutoff')}
                  </Text>
                  <Text
                    variant="body-medium"
                    fontFamily="montserrat"
                    color={colors.primary.dash}
                    style={{ fontSize: 16, fontWeight: 600 }}
                    data-testid="dashboard-last-open-cutoff-value"
                  >
                    {`${translate(`attendance:dashboard.months.${parseInt(lastOpenCutMonth, 10) - 1}`)}-${lastOpenCutYear}`}
                  </Text>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 16, paddingTop: 20 }}>
                <PrimaryButton
                  onClick={refresh}
                  iconRight={<IconScout name="sync" />}
                  disabled={!canManualRefresh || !isActiveTabReady}
                  data-testid="dashboard-refresh-button"
                >
                  {translate('attendance:dashboard.refresh')}
                </PrimaryButton>
                {lastUpdate && (
                  <Text
                    variant="small-regular"
                    color={colors.grays.neutral66}
                    data-testid="dashboard-last-update"
                  >
                    {translate('attendance:dashboard.lastUpdate', { datetime: lastUpdate })}
                  </Text>
                )}
              </div>

              <TabList
                tabs={tabs}
                defaultActiveTab={activeTab}
                onTabChange={(tabId) => setActiveTab(tabId as DashboardTab)}
                data-testid="dashboard-tab-list"
              />
            </div>
          </>
        )}
      </div>
    </PageLayout>
  );
};

export default AttendanceDashboard;
